import { io, type Socket } from 'socket.io-client';
import type {
  AlertDto,
  TransactionDto,
  ReconciliationDto,
  RealtimeEventName,
} from '@guard/shared';

const STORAGE_KEY = 'guard.auth.token';

let socket: Socket | null = null;
type Listener<T> = (payload: T) => void;
const listeners = new Map<RealtimeEventName, Listener<unknown>[]>();

export function initSocket(): Socket | null {
  const token = localStorage.getItem(STORAGE_KEY);
  if (!token) return null;
  // Reuse the existing instance (connecting OR connected). socket.io drives its
  // own (re)connection; tearing down a CONNECTING socket here caused repeated
  // "WebSocket is closed before the connection is established" when several
  // stores call onRealtime() during startup. Reset only via disconnectSocket().
  if (socket) return socket;
  socket = io('/', {
    path: '/api/socket.io',
    transports: ['websocket', 'polling'],
    auth: { token },
  });
  socket.on('connect_error', (err) => {
    if (err?.message === 'unauthorized') {
      localStorage.removeItem(STORAGE_KEY);
      const path = window.location.pathname;
      if (!path.startsWith('/login') && !path.startsWith('/setup/')) {
        window.location.assign('/login');
      }
    }
  });
  for (const [event, arr] of listeners) {
    for (const fn of arr) socket.on(event, fn);
  }
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function onRealtime<T>(event: RealtimeEventName, listener: Listener<T>): () => void {
  const s = initSocket();
  if (s) s.on(event, listener as Listener<unknown>);
  const arr = listeners.get(event) ?? [];
  arr.push(listener as Listener<unknown>);
  listeners.set(event, arr);
  return () => {
    if (socket) socket.off(event, listener as Listener<unknown>);
    const current = listeners.get(event);
    if (current) {
      listeners.set(
        event,
        current.filter((fn) => fn !== listener),
      );
    }
  };
}

export type {
  AlertDto,
  TransactionDto,
  ReconciliationDto,
  RealtimeEventName,
};
